import { createComponentKey, getComponentKeys } from '@ben/columbia/api';
import Select from '@ben/orion/deprecated-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { ZodError } from 'zod';
import z from 'zod';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';

const newKeySchema = z
  .string()
  .toLowerCase()
  .trim()
  .max(250, { message: 'Key must be less than 250 characters' })
  .regex(/^[\d_a-z]+$/, {
    message:
      'Key must be lowercase and only contain numbers, characters and underscores.',
  });

type NewKey = z.infer<typeof newKeySchema>;

interface IComponentKeyProps {
  componentControllerName: string;
}

const ComponentKey = ({ componentControllerName }: IComponentKeyProps) => {
  const { control, setValue } = useFormContext();

  const value = useWatch({
    name: `${componentControllerName}.key`,
    exact: true,
  }) as string;
  const relatedKey = useWatch({
    name: `${componentControllerName}.relatedKey`,
    exact: true,
  })?.toString();

  const [localValidation, setLocalValidation] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: '',
  });

  const [searchKey, setSearchKey] = useState('');

  const queryClient = useQueryClient();

  const dropdownQueryKey = 'dropdownOptionsFor-component-keys';

  const { data: componentKeys } = useQuery({
    queryKey: [dropdownQueryKey, { searchKey }],
    queryFn: () => getComponentKeys(searchKey),
    select: (data: { results: { key: string; id: number }[] }) => {
      const incomingResults = data.results.map(
        ({ key, id }: { key: string; id: number }) => ({
          label: key,
          key: id.toString(),
        })
      );
      /**
       * If the value is not in the dropdown options, add it to the dropdown options
       * This is to handle the case where the preselected value not in the results
       * on the first page from the API
       */
      if (
        !incomingResults.some(({ key }) => key === relatedKey) &&
        !!relatedKey
      ) {
        const existingItem = { label: value, key: relatedKey };
        return [existingItem, ...incomingResults];
      }
      return incomingResults;
    },
  });

  /**
   * Currently many of the components return null for relatedKey
   * This is a temporary fix to handle the case where the relatedKey is null
   * until the API is updated to return the relatedKey
   */
  const idValue = useMemo(() => {
    if (relatedKey) {
      return relatedKey;
    }
    return componentKeys
      ?.find(({ label }) => {
        return label === value;
      })
      ?.key.toString();
  }, [componentKeys, relatedKey, value]);

  const createNewKeyMutation = useMutation({
    mutationFn: (newKey: NewKey) => createComponentKey(newKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries([dropdownQueryKey]);
    },
    onError: (error: { response?: { data?: { key?: string[] } } }) => {
      toast.error(error?.response?.data?.key?.[0] || 'An error occurred');
    },
  });

  const handleSearchChange = debounce((search: string) => {
    setSearchKey(search);
    setLocalValidation({ hasError: false, message: '' });
  }, 100);

  const handleCreateOption = useCallback(
    (newKey: NewKey) => {
      if (componentKeys?.find(({ label }) => label === newKey)) {
        toast.error('Component key already exists');
        return;
      }

      const validationResult = newKeySchema.safeParse(newKey);
      if (validationResult.success) {
        createNewKeyMutation.mutate(newKey);
      } else {
        const error = validationResult.error as ZodError;
        setLocalValidation({
          hasError: true,
          message: error.issues[0].message,
        });
      }
    },
    [componentKeys, createNewKeyMutation]
  );

  const handleOnChange = useCallback(() => {
    setLocalValidation({ hasError: false, message: '' });
    handleSearchChange('');
  }, [handleSearchChange]);

  return (
    <Controller
      key={componentControllerName}
      name={`${componentControllerName}.key`}
      control={control}
      render={({ field, fieldState }) => (
        <Select
          options={componentKeys || []}
          name="Component key"
          onChange={(selected) => {
            const selectedOption = componentKeys?.find(
              ({ key }) => key === selected
            );
            const selectedValue = selectedOption?.label;
            field.onChange(selectedValue);
            setValue(`${componentControllerName}.relatedKey`, selected);
            handleOnChange();
          }}
          hasCreationError={localValidation.hasError}
          hasError={!!fieldState.error || localValidation.hasError}
          errorMessage={fieldState.error?.message || localValidation.message}
          defaultValue={idValue}
          value={idValue}
          showSearch
          onSearchChange={handleSearchChange}
          canCreateOption
          onCreateOption={handleCreateOption}
          data-testid="component-key-input"
        />
      )}
      rules={{
        required: fieldCannotBeBlankErrorMessage,
      }}
    />
  );
};

export default memo(ComponentKey);
